import { useEffect, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Row,
  Col,
  Collapse,
} from "reactstrap";

const DAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const timeToMinutes = (time) => {
  if (!time) return 0;
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
};

const minutesToTime = (minutes) => {
  const h = String(Math.floor(minutes / 60)).padStart(2, "0");
  const m = String(minutes % 60).padStart(2, "0");
  return `${h}:${m}`;
};

const AddEditAvailabilityModal = ({
  mode,
  open,
  onClose,
  formik,
  user,
}) => {
  const isEditMode = mode === "edit";

  // State for copy functionality
  const [copySourceDay, setCopySourceDay] = useState("");
  const [copyTargetDays, setCopyTargetDays] = useState({});
  const [isCopySectionOpen, setIsCopySectionOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      // Reset copy state when modal closes
      setCopySourceDay("");
      setCopyTargetDays({});
      setIsCopySectionOpen(false);
      return;
    }

    if (user && formik.values.person_id !== user.id) {
      formik.setFieldValue("person_id", user.id, false);
    }

    if (user && formik.values.role !== user.role) {
      formik.setFieldValue("role", user.role, false);
    }

    if (!formik.values.availability || formik.values.availability.length === 0) {
      formik.setFieldValue(
        "availability",
        DAYS.map((day) => ({
          day,
          slots: [],
        })),
        false
      );
    }
  }, [open, user, formik]);

  const handleAddSlot = (dayIndex) => {
    const availability = [...formik.values.availability];
    availability[dayIndex].slots.push({
      start_time: 540, // 09:00
      end_time: 1020, // 17:00
    });
    formik.setFieldValue("availability", availability);
  };

  const handleRemoveSlot = (dayIndex, slotIndex) => {
    const availability = [...formik.values.availability];
    availability[dayIndex].slots.splice(slotIndex, 1);
    formik.setFieldValue("availability", availability);
  };

  const handleTimeChange = (dayIndex, slotIndex, field, value) => {
    const availability = [...formik.values.availability];
    availability[dayIndex].slots[slotIndex][field] = timeToMinutes(value);
    formik.setFieldValue("availability", availability);
  };

  const daysWithSlots = formik.values.availability
    ?.map((dayObj) => ({
      name: dayObj.day,
      slotCount: dayObj.slots.length,
    }))
    .filter((d) => d.slotCount > 0) || [];

  const handleCopySlots = () => {
    if (!copySourceDay) return;

    const sourceIndex = formik.values.availability.findIndex(
      (d) => d.day === copySourceDay
    );
    if (sourceIndex === -1) return;

    const sourceSlots = [...formik.values.availability[sourceIndex].slots];

    const newAvailability = [...formik.values.availability];
    Object.keys(copyTargetDays).forEach((targetDay) => {
      if (copyTargetDays[targetDay]) {
        const targetIndex = newAvailability.findIndex((d) => d.day === targetDay);
        if (targetIndex !== -1) {
          newAvailability[targetIndex].slots = sourceSlots.map((slot) => ({
            ...slot,
          }));
        }
      }
    });

    formik.setFieldValue("availability", newAvailability);

    // Reset copy selection
    setCopyTargetDays({});
    setIsCopySectionOpen(false);
  };

  const toggleTargetDay = (day) => {
    setCopyTargetDays((prev) => ({
      ...prev,
      [day]: !prev[day],
    }));
  };

  return (
    <Modal isOpen={open} toggle={onClose} size="xl">
      <Form onSubmit={formik.handleSubmit}>
        <ModalHeader toggle={onClose}>
          {isEditMode ? "Edit My Availability" : "Set My Availability"}
        </ModalHeader>

        <ModalBody>
          {/* Hidden fields */}
          <input type="hidden" name="role" value={user?.role || ""} />
          <input type="hidden" name="person_id" value={user?.id || ""} />

          {/* Days Slots Input */}
          {formik.values.availability?.map((dayObj, dayIndex) => (
            <div key={dayObj.day} className="border rounded p-3 mb-3">
              <Row className="align-items-center mb-2">
                <Col>
                  <strong className="text-capitalize">{dayObj.day}</strong>
                  {dayObj.slots.length > 0 && (
                    <small className="text-muted ms-2">
                      ({dayObj.slots.length} slot{dayObj.slots.length !== 1 ? "s" : ""})
                    </small>
                  )}
                </Col>
                <Col className="text-end">
                  <Button
                    size="sm"
                    color="success"
                    type="button"
                    onClick={() => handleAddSlot(dayIndex)}
                  >
                    + Add Slot
                  </Button>
                </Col>
              </Row>

              {dayObj.slots.length === 0 && (
                <div className="text-muted small">No slots added</div>
              )}

              {dayObj.slots.map((slot, slotIndex) => (
                <Row key={slotIndex} className="mb-2 align-items-center">
                  <Col md={4}>
                    <FormGroup>
                      <Label className="small mb-1">Start Time</Label>
                      <Input
                        type="time"
                        value={minutesToTime(slot.start_time)}
                        onChange={(e) =>
                          handleTimeChange(
                            dayIndex,
                            slotIndex,
                            "start_time",
                            e.target.value
                          )
                        }
                      />
                    </FormGroup>
                  </Col>

                  <Col md={4}>
                    <FormGroup>
                      <Label className="small mb-1">End Time</Label>
                      <Input
                        type="time"
                        value={minutesToTime(slot.end_time)}
                        onChange={(e) =>
                          handleTimeChange(
                            dayIndex,
                            slotIndex,
                            "end_time",
                            e.target.value
                          )
                        }
                      />
                    </FormGroup>
                  </Col>

                  <Col md={4}>
                    <Button
                      color="danger"
                      size="sm"
                      type="button"
                      onClick={() => handleRemoveSlot(dayIndex, slotIndex)}
                    >
                      Remove
                    </Button>
                  </Col>
                </Row>
              ))}
            </div>
          ))}

          {/* Copy Slots Section */}
          {daysWithSlots.length > 0 && (
            <div className="border rounded p-3 mb-4 bg-light">
              <div
                className="d-flex justify-content-between align-items-center cursor-pointer"
                onClick={() => setIsCopySectionOpen(!isCopySectionOpen)}
                style={{ cursor: "pointer" }}
              >
                <strong>Copy Time Slots to Other Days</strong>
                <Button size="sm" color="link" className="p-0">
                  {isCopySectionOpen ? "▲" : "▼"}
                </Button>
              </div>

              <Collapse isOpen={isCopySectionOpen}>
                <Row className="mt-3">
                  <Col md={6}>
                    <FormGroup>
                      <Label>Copy from day</Label>
                      <Input
                        type="select"
                        value={copySourceDay}
                        onChange={(e) => setCopySourceDay(e.target.value)}
                      >
                        <option value="">-- Select a day --</option>
                        {daysWithSlots.map((d) => (
                          <option key={d.name} value={d.name}>
                            {d.name.charAt(0).toUpperCase() + d.name.slice(1)} ({d.slotCount} slot{d.slotCount !== 1 ? "s" : ""})
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>

                  <Col md={6}>
                    <FormGroup>
                      <Label>Copy to days (select multiple)</Label>
                      <div className="border rounded p-2" style={{ maxHeight: "150px", overflowY: "auto" }}>
                        {DAYS.map((day) => {
                          const isSource = day === copySourceDay;
                          const hasSlots = formik.values.availability
                            ?.find((d) => d.day === day)
                            ?.slots.length > 0;

                          return (
                            <div key={day} className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`copy-${day}`}
                                checked={!!copyTargetDays[day]}
                                onChange={() => toggleTargetDay(day)}
                                disabled={isSource}
                              />
                              <label
                                className="form-check-label text-capitalize"
                                htmlFor={`copy-${day}`}
                                style={{
                                  opacity: isSource ? 0.5 : 1,
                                  fontStyle: hasSlots ? "normal" : "italic",
                                }}
                              >
                                {day} {isSource && "(source)"}
                                {hasSlots && " (has slots)"}
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    </FormGroup>
                  </Col>
                </Row>

                <div className="text-end">
                  <Button
                    color="info"
                    size="sm"
                    onClick={handleCopySlots}
                    disabled={!copySourceDay || Object.values(copyTargetDays).every((v) => !v)}
                  >
                    Copy Slots to Selected Days
                  </Button>
                </div>
              </Collapse>
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          <Button type="submit" color="primary" disabled={formik.isSubmitting}>
            {isEditMode ? "Update" : "Save"}
          </Button>
          <Button color="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export { AddEditAvailabilityModal };